// src/components/partner/PartnersSearchPanel.tsx
//
// PartnersSearchPanel
// - Presentation-only search UI for Partners page.
// - Delegates all state ownership to the parent page.
// - Uses the shared SearchPanel container for:
//   - Search / Reset buttons (single source of truth)
//   - Optional auto-search (debounced) controlled by the page via `autoSearch` prop
//   - Active filter chips (removable, with readable labels)
//
// IMPORTANT:
// - Do NOT hardcode autoSearch defaults here.
//   SearchPanel owns global defaults (enabled: false, delayMs: 500) via ./constants.
//   This component must only pass through the `autoSearch` prop if provided by the page.

import React, { useMemo } from 'react';
import { Grid } from '@mui/material';

import {
  SearchPanel,
  SearchTextField,
  type ActiveChipsConfig,
  type SearchChipSpec,
  type SetSearchValueFn,
} from '@components/common/search';

import type { PartnersSearchForm } from '@models/partner';

type PartnersSearchPanelProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  /**
   * Draft search state bound to inputs.
   * Chips reflect this draft state by design.
   */
  searchForm: PartnersSearchForm;
  setValue: SetSearchValueFn<PartnersSearchForm>;

  onSearch: () => void;
  onReset: () => void;

  /**
   * Auto-search configuration provided by the page.
   * If omitted, SearchPanel falls back to its global defaults.
   */
  autoSearch?: { enabled: boolean; delayMs?: number };
};

const PartnersSearchPanel: React.FC<PartnersSearchPanelProps> = ({
  open,
  onOpenChange,
  searchForm,
  setValue,
  onSearch,
  onReset,
  autoSearch,
}) => {
  /* ------------------------------------------------------------------ */
  /* Active chips configuration                                          */
  /* ------------------------------------------------------------------ */

  const chips = useMemo<SearchChipSpec<PartnersSearchForm>[]>(
    () => [
      {
        key: 'name',
        label: 'Partner Name',
        getValueText: (s) => (s.name ?? '').trim(),
        clear: ({ setValue }) => setValue('name', null),
      },
      {
        key: 'manager',
        label: 'Account Manager',
        getValueText: (s) => (s.manager ?? '').trim(),
        clear: ({ setValue }) => setValue('manager', null),
      },
      {
        key: 'email',
        label: 'Email',
        getValueText: (s) => (s.email ?? '').trim(),
        clear: ({ setValue }) => setValue('email', null),
      },
      {
        key: 'address',
        label: 'Address',
        getValueText: (s) => (s.address ?? '').trim(),
        clear: ({ setValue }) => setValue('address', null),
      },
    ],
    [],
  );

  const chipsConfig = useMemo<ActiveChipsConfig<PartnersSearchForm>>(
    () => ({
      enabled: true,
      search: searchForm,
      setValue,
      chips,
      maxVisible: 2,
      // Keep behavior consistent across pages:
      // chip removal should immediately apply (manual / auto-search is optional).
      onAfterClear: onSearch,
    }),
    [chips, onSearch, searchForm, setValue],
  );

  // ✅ 지침 강화: enabled=true인 경우에만 패스스루
  const panelAutoSearch = autoSearch?.enabled === true ? autoSearch : undefined;

  return (
    <SearchPanel
      title="Filters"
      open={open}
      onOpenChange={onOpenChange}
      onSearch={onSearch}
      onReset={onReset}
      chipsConfig={chipsConfig}
      // Pass-through only. If undefined, SearchPanel uses global defaults.
      autoSearch={panelAutoSearch}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            fullWidth
            label="Partner Name"
            placeholder="Enter partner name"
            size="small"
            value={searchForm.name ?? ''}
            onChangeValue={(v) => setValue('name', v)}
            onClear={() => setValue('name', null)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            fullWidth
            label="Account Manager"
            placeholder="Enter manager name"
            size="small"
            value={searchForm.manager ?? ''}
            onChangeValue={(v) => setValue('manager', v)}
            onClear={() => setValue('manager', null)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            fullWidth
            label="Email"
            placeholder="Enter email"
            size="small"
            value={searchForm.email ?? ''}
            onChangeValue={(v) => setValue('email', v)}
            onClear={() => setValue('email', null)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            fullWidth
            label="Address"
            placeholder="Enter address"
            size="small"
            value={searchForm.address ?? ''}
            onChangeValue={(v) => setValue('address', v)}
            onClear={() => setValue('address', null)}
          />
        </Grid>
      </Grid>
    </SearchPanel>
  );
};

export default PartnersSearchPanel;
